import React from 'react';
import { Settings2, Pause, RotateCw, Activity, Save, Maximize, X, Fish, Waves } from 'lucide-react';
interface Props {
  onClose: () => void;
}
export function HowToPage({
  onClose
}: Props) {
  return <div className="h-full w-full bg-[#050b14] text-white overflow-y-auto font-sans p-6 md:p-12" data-id="element-314">
      <div className="max-w-3xl mx-auto" data-id="element-315">
        <div className="flex items-center justify-between mb-8 pb-4 border-b border-[#00f0ff]/20" data-id="element-316">
          <div data-id="element-317">
            <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3" data-id="element-318">
              <Fish className="text-[#00f0ff]" size={32} data-id="element-319" />
              How to Use the Programmable Bobber
            </h1>
            <p className="text-[#00f0ff]/70 font-mono text-sm" data-id="element-320">
              Complete guide to programming your armature movements
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors text-white/70 hover:text-white" aria-label="Close instructions" data-id="element-321">
            <X size={24} data-id="element-322" />
          </button>
        </div>

        <div className="space-y-8" data-id="element-323">
          {/* Armature Settings */}
          <section className="bg-[#0a1628] border border-[#00f0ff]/20 rounded-xl p-6 shadow-[0_0_15px_rgba(0,240,255,0.05)]" data-id="element-324">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2" data-id="element-325">
              <Settings2 className="text-[#00f0ff]" data-id="element-326" />
              Armature Settings
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-327">
              <div data-id="element-328">
                <h3 className="text-[#00f0ff] font-bold mb-1" data-id="element-329">
                  Armature Speed
                </h3>
                <p className="text-white/70 text-sm" data-id="element-330">
                  Controls how fast the armature swings back and forth. Adjust
                  from slow (1) to fast (10) to match the desired action for
                  your lure.
                </p>
              </div>
              <div data-id="element-331">
                <h3 className="text-[#00f0ff] font-bold mb-1 flex items-center gap-1" data-id="element-332">
                  <RotateCw size={14} data-id="element-333" /> Swing Angle
                </h3>
                <p className="text-white/70 text-sm" data-id="element-334">
                  Sets how wide the armature swings. A narrow angle creates
                  tight, subtle movements, while a wide angle creates broad
                  sweeps.
                </p>
              </div>
              <div className="md:col-span-2" data-id="element-335">
                <h3 className="text-[#00f0ff] font-bold mb-1" data-id="element-336">Sweep Style</h3>
                <p className="text-white/70 text-sm" data-id="element-337">
                  Changes the smoothness of the swing. "Smooth" creates a
                  continuous pendulum motion. "Jerky" adds micro-stops along the
                  sweep to simulate an injured baitfish.
                </p>
              </div>
            </div>
          </section>

          {/* Pause & Jig */}
          <section className="bg-[#0a1628] border border-[#00f0ff]/20 rounded-xl p-6 shadow-[0_0_15px_rgba(0,240,255,0.05)]" data-id="element-338">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2" data-id="element-339">
              <Pause className="text-[#00f0ff]" data-id="element-340" />
              Pause & Jig Controls
            </h2>
            <div className="space-y-4" data-id="element-341">
              <div data-id="element-342">
                <h3 className="text-[#00f0ff] font-bold mb-1" data-id="element-343">
                  Pause at Top of Swing
                </h3>
                <p className="text-white/70 text-sm" data-id="element-344">
                  When enabled, the armature will stop at the peak of its swing
                  before returning. You can set the exact angle where it pauses
                  and how long the pause lasts (up to 60 seconds).
                </p>
              </div>
              <div className="pl-4 border-l-2 border-[#00f0ff]/30 mt-4" data-id="element-345">
                <h3 className="text-[#00f0ff] font-bold mb-1 flex items-center gap-1" data-id="element-346">
                  <Activity size={14} data-id="element-347" /> Jig During Pause
                </h3>
                <p className="text-white/70 text-sm mb-2" data-id="element-348">
                  Adds vertical jigging motion while the armature is paused.
                </p>
                <ul className="list-disc list-inside text-white/70 text-sm space-y-1 ml-2" data-id="element-349">
                  <li data-id="element-350">
                    <strong className="text-white/90" data-id="element-351">Distance:</strong> How far
                    the lure pops up
                  </li>
                  <li data-id="element-352">
                    <strong className="text-white/90" data-id="element-353">Speed Up/Down:</strong>{' '}
                    Control the snap up and flutter down speeds independently
                  </li>
                  <li data-id="element-354">
                    <strong className="text-white/90" data-id="element-355">Pause Between:</strong>{' '}
                    Add a brief pause at the top of the jig before it falls
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Presets & Display */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-356">
            <section className="bg-[#0a1628] border border-[#00f0ff]/20 rounded-xl p-6 shadow-[0_0_15px_rgba(0,240,255,0.05)]" data-id="element-357">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2" data-id="element-358">
                <Save className="text-[#00f0ff]" data-id="element-359" />
                Saving Presets
              </h2>
              <p className="text-white/70 text-sm mb-4" data-id="element-360">
                You can save up to 10 custom movement patterns.
              </p>
              <ol className="list-decimal list-inside text-white/70 text-sm space-y-2" data-id="element-361">
                <li data-id="element-362">Adjust the settings to create your desired pattern</li>
                <li data-id="element-363">
                  In the water view, click a number (1-10) in the "SAVE" row
                </li>
                <li data-id="element-364">The button will light up green, indicating it's saved</li>
                <li data-id="element-365">Click any saved number to instantly load those settings</li>
              </ol>
            </section>

            <section className="bg-[#0a1628] border border-[#00f0ff]/20 rounded-xl p-6 shadow-[0_0_15px_rgba(0,240,255,0.05)]" data-id="element-366">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2" data-id="element-367">
                <Maximize className="text-[#00f0ff]" data-id="element-368" />
                Fullscreen Mode
              </h2>
              <p className="text-white/70 text-sm mb-4" data-id="element-369">
                Click the expand icon in the bottom right of the water view to
                enter fullscreen mode.
              </p>
              <ul className="list-disc list-inside text-white/70 text-sm space-y-2" data-id="element-370">
                <li data-id="element-371">Watch your programmed pattern in detail</li>
                <li data-id="element-372">
                  Use the "PLAY" row at the top to switch between your saved
                  presets
                </li>
                <li data-id="element-373">Adjust lure size and armature length using the top menu</li>
              </ul>
            </section>
          </div>

          {/* Lure & Depth */}
          <section className="bg-[#0a1628] border border-[#00f0ff]/20 rounded-xl p-6 shadow-[0_0_15px_rgba(0,240,255,0.05)]" data-id="element-374">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2" data-id="element-375">
              <Waves className="text-[#00f0ff]" data-id="element-376" />
              Lure & Depth
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-377">
              <div data-id="element-378">
                <h3 className="text-[#00f0ff] font-bold mb-1" data-id="element-379">Depth Control</h3>
                <p className="text-white/70 text-sm" data-id="element-380">
                  Use the slider at the bottom of the control panel to set the
                  bobber stop depth (1-20ft). This determines how deep your lure
                  sits.
                </p>
              </div>
              <div data-id="element-381">
                <h3 className="text-[#00f0ff] font-bold mb-1" data-id="element-382">Sizes</h3>
                <p className="text-white/70 text-sm" data-id="element-383">
                  Use the overlay menu in the water view to change the Lure Size
                  (XS-XL) and Armature Length (XS-XXL) to match your physical
                  setup.
                </p>
              </div>
            </div>
          </section>
        </div>

        <div className="mt-12 text-center pb-12" data-id="element-384">
          <button onClick={onClose} className="px-8 py-3 bg-[#00f0ff] text-[#050b14] font-bold rounded-lg hover:bg-white transition-colors shadow-[0_0_15px_rgba(0,240,255,0.4)]" data-id="element-385">
            Return to App
          </button>
        </div>
      </div>
    </div>;
}